<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header-admin></vue-header-admin>
    <v-main class="mb-10">
      <v-container fluid class="px-6 py-6">
        <v-row>
          <v-col cols="12" md="8">
            <h1 class="text-h4 font-weight-bold text-typo mb-4">Spinning Wheel Configuration</h1>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedCategory"
              :items="categoryOptions"
              label="Category"
              outlined
              dense
              @change="onCategoryChange"
              :loading="loading"
              :disabled="loading"
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <!-- Configuration Panel -->
          <v-col cols="12" md="7">
            <v-card class="card-shadow border-radius-xl mb-4">
              <v-card-title class="d-flex justify-space-between">
                <span>Wheel Segments</span>
                <v-btn color="green" dark @click="addNewSegment">
                  <v-icon left class="material-icons-round">add</v-icon>
                  Add Segment
                </v-btn>
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :headers="segmentHeaders"
                  :items="wheelSegments"
                  :items-per-page="10"
                  class="elevation-1"
                >
                  <template v-slot:item.label="{ item }">
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <div class="text-truncate" style="max-width: 150px;" v-bind="attrs" v-on="on">
                          {{ item.label }}
                        </div>
                      </template>
                      <span>{{ item.label }}</span>
                    </v-tooltip>
                  </template>

                  <template v-slot:item.color="{ item }">
                    <div class="color-preview" :style="{ backgroundColor: item.color }"></div>
                    {{ item.color }}
                  </template>

                  <template v-slot:item.details="{ item }">
                    <span v-if="item.type === 'discount'">
                      {{ item.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount' }}
                      <br>
                      <small>Code: {{ item.code }}</small>
                    </span>
                    <span v-else-if="item.type === 'gift_card'">
                      <small>Code: {{ item.code }}</small>
                    </span>
                    <span v-else-if="item.type === 'custom'">
                      <small>{{ item.description }}</small>
                    </span>
                  </template>

                  <template v-slot:item.value="{ item }">
                    <span v-if="item.type === 'discount'">
                      {{ item.discountType === 'percentage' ? item.value + '%' : '$' + item.value }}
                    </span>
                    <span v-else-if="item.type === 'gift_card'">
                      ${{ item.value }}
                    </span>
                    <span v-else-if="item.type === 'points'">
                      {{ item.value }}
                    </span>
                  </template>

                  <template v-slot:item.probability="{ item }">
                    {{ (item.probability * 100).toFixed(1) }}%
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn icon small @click="editSegmentById(item.id)">
                      <v-icon small class="material-icons-round">edit</v-icon>
                    </v-btn>
                    <v-btn icon small @click="deleteSegmentById(item.id)">
                      <v-icon small class="material-icons-round">delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>

            <v-card class="card-shadow border-radius-xl mb-5">
              <v-card-title>Wheel Settings</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="wheelSettings.title"
                      label="Wheel Title"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="wheelSettings.spinDuration"
                      :items="spinDurationOptions"
                      label="Spin Duration"
                      outlined
                      dense
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="wheelSettings.spinLimit"
                      :items="spinLimitOptions"
                      label="Spin Limit Per User"
                      outlined
                      dense
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="wheelSettings.spinLimitPeriod"
                      :items="spinLimitPeriodOptions"
                      label="Spin Limit Period"
                      outlined
                      dense
                      :disabled="wheelSettings.spinLimit === 0"
                    ></v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-switch
                      v-model="wheelSettings.requireLogin"
                      label="Require Login to Spin"
                      color="green"
                    ></v-switch>
                  </v-col>
                  <v-col cols="12">
                    <v-switch
                      v-model="wheelSettings.active"
                      label="Wheel Active"
                      color="success"
                    ></v-switch>
                  </v-col>

                  <!-- Jackpot-specific settings -->
                  <v-col cols="12" v-if="selectedCategory === 'jackpot'">
                    <v-divider class="mb-4"></v-divider>
                    <h3 class="text-h6 mb-3">Jackpot Settings</h3>

                    <v-text-field
                      v-model.number="wheelSettings.referrals_for_jackpot"
                      label="Referrals Needed for Jackpot Spin"
                      type="number"
                      min="1"
                      max="100"
                      outlined
                      dense
                      hint="Number of referrals a user needs to earn a jackpot spin"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="green" dark @click="saveSettings">
                  <v-icon left class="material-icons-round">save</v-icon>
                  Save Settings
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <!-- Preview Panel -->
          <v-col cols="12" md="5">
            <v-card class="card-shadow border-radius-xl">
              <v-card-title>Wheel Preview</v-card-title>
              <v-card-text class="text-center">
                <spinning-wheel
                  :segments="previewSegments"
                  :colors="previewColors"
                  @prize-won="onPrizeWon"
                ></spinning-wheel>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Segment Edit Dialog -->
      <v-dialog v-model="editDialog" max-width="600px">
        <v-card>
          <v-card-title>
            {{ editIndex === -1 ? 'Add New Segment' : 'Edit Segment' }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedSegment.label"
                  label="Label"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'Label is required', v => v.length <= 20 || 'Label should be 20 characters or less']"
                  counter="20"
                  hint="Keep labels short for better display on the wheel"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedSegment.type"
                  :items="segmentTypeOptions"
                  label="Type"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col>

              <!-- Discount Fields -->
              <template v-if="editedSegment.type === 'discount'">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editedSegment.discountType"
                    :items="discountTypeOptions"
                    label="Discount Type"
                    outlined
                    dense
                    required
                  ></v-select>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editedSegment.value"
                    :label="editedSegment.discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'"
                    type="number"
                    :min="1"
                    :max="editedSegment.discountType === 'percentage' ? 100 : 1000"
                    outlined
                    dense
                    :suffix="editedSegment.discountType === 'percentage' ? '%' : '$'"
                    required
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editedSegment.code"
                    label="Discount Code"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
              </template>

              <!-- Points Fields -->
              <template v-if="editedSegment.type === 'points'">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editedSegment.value"
                    label="Points Amount"
                    type="number"
                    min="1"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
              </template>

              <!-- Gift Card Fields -->
              <template v-if="editedSegment.type === 'gift_card'">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editedSegment.value"
                    label="Gift Card Value"
                    type="number"
                    min="1"
                    outlined
                    dense
                    prefix="$"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editedSegment.code"
                    label="Gift Card Code Prefix"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
              </template>

              <!-- Custom Fields -->
              <template v-if="editedSegment.type === 'custom'">
                <v-col cols="12">
                  <v-textarea
                    v-model="editedSegment.description"
                    label="Custom Prize Description"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'Description is required', v => v.length <= 100 || 'Description should be 100 characters or less']"
                    counter="100"
                    hint="Keep descriptions concise for better user experience"
                    persistent-hint
                  ></v-textarea>
                </v-col>
              </template>

              <!-- Common Fields -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="editedSegment.probability"
                  label="Probability"
                  type="number"
                  min="0.01"
                  max="1"
                  step="0.01"
                  outlined
                  dense
                  hint="Value between 0.01 and 1 (e.g., 0.25 = 25%)"
                  persistent-hint
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-color-picker
                  v-model="editedSegment.color"
                  mode="hexa"
                  hide-canvas
                  hide-inputs
                  show-swatches
                  swatches-max-height="150px"
                ></v-color-picker>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeEdit">Cancel</v-btn>
            <v-btn color="green" dark @click="saveSegment">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400px">
        <v-card>
          <v-card-title>Delete Segment</v-card-title>
          <v-card-text>
            Are you sure you want to delete this segment?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="red" dark @click="confirmDelete">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
    <vue-footer-admin></vue-footer-admin>
  </v-app>
</template>

<script>
import SpinningWheel from '@/components/Games/SpinningWheel.vue';
import ClipLoader from 'vue-spinner/src/PulseLoader.vue';

export default {
  name: 'SpinningWheelAdmin',
  components: {
    SpinningWheel,
    'RotateLoader': ClipLoader
  },
  data() {
    return {
      loading: false,
      selectedCategory: 'rental',
      categoryOptions: [
        { text: 'Rental', value: 'rental' },
        { text: 'Inventory', value: 'inventory' },
        { text: 'Jackpot', value: 'jackpot' }
      ],
      wheelSegments: [],
      wheelSettings: {
        title: 'Spin to Win',
        spinDuration: 5,
        spinLimit: 1,
        spinLimitPeriod: 'day',
        requireLogin: true,
        active: true,
        category: 'rental',
        referrals_for_jackpot: 5
      },

      // Edit dialog
      editDialog: false,
      editIndex: -1,
      editedSegment: {
        label: '',
        type: 'discount',
        discountType: 'percentage', // New field for discount type (percentage or fixed)
        value: 10,
        code: '',
        description: '',
        probability: 0.1,
        color: '#FF6384'
      },
      defaultSegment: {
        id: Math.floor(Math.random() *44485555),
        label: '',
        type: 'discount',
        discountType: 'percentage', // New field for discount type (percentage or fixed)
        value: 10,
        code: '',
        description: '',
        probability: 0.1,
        color: '#FF6384'
      },

      // Delete dialog
      deleteDialog: false,
      deleteIndex: -1,

      // Table headers
      segmentHeaders: [
        { text: 'Label', value: 'label' },
        { text: 'Type', value: 'type' },
        { text: 'Value', value: 'value' },
        { text: 'Details', value: 'details' },
        { text: 'Color', value: 'color' },
        { text: 'Probability', value: 'probability' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],

      // Options
      segmentTypeOptions: [
        { text: 'Discount', value: 'discount' },
        { text: 'Points', value: 'points' },
        { text: 'Gift Card', value: 'gift_card' },
        { text: 'Custom', value: 'custom' }
      ],

      // Discount type options
      discountTypeOptions: [
        { text: 'Percentage (%)', value: 'percentage' },
        { text: 'Fixed Amount ($)', value: 'fixed' }
      ],
      spinDurationOptions: [
        { text: '3 seconds', value: 3 },
        { text: '5 seconds', value: 5 },
        { text: '7 seconds', value: 7 },
        { text: '10 seconds', value: 10 }
      ],
      spinLimitOptions: [
        { text: 'No Limit', value: 0 },
        { text: '1 spin', value: 1 },
        { text: '2 spins', value: 2 },
        { text: '3 spins', value: 3 },
        { text: '5 spins', value: 5 },
        { text: '10 spins', value: 10 }
      ],
      spinLimitPeriodOptions: [
        { text: 'Per Day', value: 'day' },
        { text: 'Per Week', value: 'week' },
        { text: 'Per Month', value: 'month' },
        { text: 'Forever', value: 'forever' }
      ]
    };
  },
  computed: {
    previewSegments() {
      return this.wheelSegments.map(segment => ({
        label: segment.label,
        probability: segment.probability,
        type: segment.type,
        value: segment.value,
        description: this.getSegmentDescription(segment)
      }));
    },
    previewColors() {
      return this.wheelSegments.map(segment => segment.color);
    },
    totalProbability() {
      return this.wheelSegments.reduce((sum, segment) => sum + parseFloat(segment.probability || 0), 0);
    }
  },
  created() {
    this.loadWheelData();
  },
  watch: {
    'editedSegment.discountType': function(newType) {
      // Update label when discount type changes
      if (this.editedSegment.type === 'discount') {
        if (newType === 'percentage') {
          this.editedSegment.label = `${this.editedSegment.value}% OFF`;
        } else {
          this.editedSegment.label = `$${this.editedSegment.value} OFF`;
        }
      }
    },
    'editedSegment.value': function(newValue) {
      // Update label when value changes for discount segments
      if (this.editedSegment.type === 'discount') {
        if (this.editedSegment.discountType === 'percentage') {
          this.editedSegment.label = `${newValue}% OFF`;
        } else {
          this.editedSegment.label = `$${newValue} OFF`;
        }
      }
    }
  },
  methods: {
    loadWheelData() {
      this.loading = true;

      // Make sure the category is set in the settings
      this.wheelSettings.category = this.selectedCategory;

      // Reset segments array
      this.wheelSegments = [];

      // Fetch the data from the server with the category parameter
      axios.get(`/api/spinning-wheel/config?category=${this.selectedCategory}`)
        .then(response => {
          if (response.data.flag === 1) {
            this.wheelSegments = response.data.segments || [];
            this.wheelSettings = response.data.settings || this.wheelSettings;

            // Ensure the category is set correctly in the settings
            this.wheelSettings.category = this.selectedCategory;

            // Ensure all discount segments have a discountType
            this.wheelSegments.forEach(segment => {
              if (segment.type === 'discount' && !segment.discountType) {
                segment.discountType = 'percentage'; // Default to percentage for backward compatibility
              }
            });
          } else {
            Vue.$toast.error(response.data.message || 'Failed to load wheel configuration', {
              position: 'top-right'
            });
          }
        })
        .catch(error => {
          console.error('Error loading wheel data:', error);
          Vue.$toast.error('Failed to load wheel configuration', {
            position: 'top-right'
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onCategoryChange() {
      // When the category changes, load the configuration for that category
      this.loadWheelData();
    },

    saveSettings() {
      this.loading = true;

      // Validate total probability
      if (Math.abs(this.totalProbability - 1) > 0.01) {
        Vue.$toast.error('Total probability must equal 100%', {
          position: 'top-right'
        });
        this.loading = false;
        return;
      }

      // Make sure the category is set in the settings
      this.wheelSettings.category = this.selectedCategory;

      // Save the data to the server with the category parameter
      axios.post('/api/spinning-wheel/config', {
        segments: this.wheelSegments,
        settings: this.wheelSettings,
        category: this.selectedCategory
      })
        .then(response => {
          if (response.data && response.data.flag === 1) {
            Vue.$toast.success('Wheel configuration saved successfully', {
              position: 'top-right'
            });
          } else {
            Vue.$toast.error(response.data.message || 'Failed to save wheel configuration', {
              position: 'top-right'
            });
          }
        })
        .catch(error => {
          console.error('Error saving wheel data:', error);
          Vue.$toast.error('Failed to save wheel configuration', {
            position: 'top-right'
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    addNewSegment() {
      this.editIndex = -1;
      this.editedSegment = this.defaultSegment;
      this.editedSegment.id = Math.floor(Math.random() *44485555)

      // Set default label based on segment type and discount type
      if (this.editedSegment.type === 'discount') {
        if (this.editedSegment.discountType === 'percentage') {
          this.editedSegment.label = `${this.editedSegment.value}% OFF`;
        } else {
          this.editedSegment.label = `$${this.editedSegment.value} OFF`;
        }
      }

      this.editDialog = true;
    },

    editSegmentById(id) {
      // Find the segment by ID
      const index = this.wheelSegments.findIndex(segment => segment.id === id);
      if (index !== -1) {
        this.editSegment(index);
      } else {
        Vue.$toast.error('Segment not found', {
          position: 'top-right'
        });
      }
    },

    editSegment(index) {
      this.editIndex = index;
      this.editedSegment = JSON.parse(JSON.stringify(this.wheelSegments[index]));

      // Ensure discountType is set for discount segments
      if (this.editedSegment.type === 'discount' && !this.editedSegment.discountType) {
        // Default to percentage for backward compatibility
        this.editedSegment.discountType = 'percentage';
      }

      this.editDialog = true;
    },

    deleteSegmentById(id) {
      // Find the segment by ID
      const index = this.wheelSegments.findIndex(segment => segment.id == id);
      if (index !== -1) {
        this.deleteSegment(index);
      } else {
        Vue.$toast.error('Segment not found', {
          position: 'top-right'
        });
      }
    },

    deleteSegment(index) {
      this.deleteIndex = index;
      this.deleteDialog = true;
    },

    confirmDelete() {
      this.wheelSegments.splice(this.deleteIndex, 1);
      this.deleteDialog = false;

      // Redistribute probabilities
      this.redistributeProbabilities();

      // Save changes to the database
      this.saveSettings();

      Vue.$toast.success('Segment deleted', {
        position: 'top-right'
      });
    },

    saveSegment() {
      // Validate inputs
      if (!this.editedSegment.label) {
        Vue.$toast.error('Label is required', {
          position: 'top-right'
        });
        return;
      }

      if (this.editedSegment.probability <= 0 || this.editedSegment.probability > 1) {
        Vue.$toast.error('Probability must be between 0.01 and 1', {
          position: 'top-right'
        });
        return;
      }

      // Format the segment based on type
      const segment = this.formatSegment(this.editedSegment);

      // Ensure segment has a unique ID
      if (!segment.id) {
        segment.id = Date.now() + Math.floor(Math.random() * 1000);
      }

      if (this.editIndex === -1) {
        // Add new segment
        this.wheelSegments.push(segment);
      } else {
        // Update existing segment
        this.wheelSegments.splice(this.editIndex, 1, segment);
      }

      // Redistribute probabilities
      this.redistributeProbabilities();

      // Close the edit dialog
      this.closeEdit();

      // Save changes to the database
      this.saveSettings();

      Vue.$toast.success('Segment saved', {
        position: 'top-right'
      });
    },

    closeEdit() {
      this.editDialog = false;
      this.editIndex = -1;
      this.editedSegment = JSON.parse(JSON.stringify(this.defaultSegment));
    },

    formatSegment(segment) {
      const formattedSegment = {
        id: segment.id || null, // Preserve ID if it exists
        label: segment.label,
        type: segment.type,
        probability: parseFloat(segment.probability),
        color: segment.color
      };

      // Add type-specific properties
      switch (segment.type) {
        case 'discount':
          formattedSegment.value = parseFloat(segment.value);
          formattedSegment.code = segment.code;
          formattedSegment.discountType = segment.discountType || 'percentage'; // Default to percentage if not specified
          break;
        case 'points':
          formattedSegment.value = parseFloat(segment.value);
          break;
        case 'gift_card':
          formattedSegment.value = parseFloat(segment.value);
          formattedSegment.code = segment.code;
          break;
        case 'custom':
          formattedSegment.description = segment.description;
          break;
      }

      return formattedSegment;
    },

    redistributeProbabilities() {
      // If total probability is not close to 1, redistribute
      if (Math.abs(this.totalProbability - 1) > 0.01) {
        const factor = 1 / this.totalProbability;
        this.wheelSegments.forEach(segment => {
          segment.probability = parseFloat((segment.probability * factor).toFixed(4));
        });
      }
    },

    // No localStorage methods needed as we're using the database only

    getSegmentDescription(segment) {
      switch (segment.type) {
        case 'discount':
          if (segment.discountType === 'fixed') {
            return `$${segment.value} discount with code ${segment.code}`;
          } else {
            return `${segment.value}% discount with code ${segment.code}`;
          }
        case 'points':
          return `${segment.value} reward points`;
        case 'gift_card':
          return `$${segment.value} gift card`;
        case 'custom':
          return segment.description;
        default:
          return '';
      }
    },

    onPrizeWon(prize, segmentIndex) {
      console.log('Prize won:', prize);
      console.log('Segment index:', segmentIndex);

      // Ensure the prize has the correct type based on the segment label
      if (prize && prize.label && prize.label.includes('Try Again')) {
        prize.type = 'try_again';
      } else if (prize && prize.label && prize.label.includes('OFF')) {
        prize.type = 'discount';

        // Determine if it's a percentage or fixed discount
        if (prize.label.includes('%')) {
          prize.discountType = 'percentage';
        } else if (prize.label.includes('$')) {
          prize.discountType = 'fixed';
        }
      }

      // In a real implementation, you might want to record this or take some action
    }
  }
};
</script>

<style scoped>
.color-preview {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  margin-right: 8px;
  vertical-align: middle;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

/* Responsive text handling */
@media (max-width: 600px) {
  .text-truncate {
    max-width: 100px !important;
  }
}
</style>
